// The API client.
//
// Same-origin: the portal and the API are served by one CloudFront distribution,
// so requests go to a relative /api path with no CORS involved. The bearer token
// is attached per request rather than held in a module variable, because
// currentSession() refreshes it when it has expired.
import { currentSession } from './auth';
import { loadConfig } from './config';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class NotSignedInError extends Error {}

const request = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
  const [config, session] = await Promise.all([loadConfig(), currentSession()]);
  if (!session) throw new NotSignedInError('Session expired');

  const response = await fetch(`${config.apiBase}${path}`, {
    method,
    headers: {
      Authorization: session.idToken,
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 401) {
    // The token was rejected outright. Treated as a signed-out state rather than
    // an error to display, so the UI sends the user back to the login screen.
    throw new NotSignedInError('Session expired');
  }
  if (!response.ok) {
    // The API answers with { message }, and those messages are written to be
    // shown to whoever triggered them.
    const detail = await response
      .json()
      .then((data: { message?: string }) => data.message)
      .catch(() => undefined);
    throw new ApiError(response.status, detail ?? `Request failed (HTTP ${response.status})`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
};

// The invite calls, and the only ones that send no Authorization header.
//
// A separate function rather than a flag on request(), so that "this call needs
// no session" is visible where it is defined and cannot be switched on by
// accident for a route that does need one. There is no 401 branch here: nothing
// was presented, so a refusal is an ordinary error to show rather than a reason
// to send someone to the login screen.
const publicRequest = async <T>(path: string, body: unknown): Promise<T> => {
  const config = await loadConfig();
  const response = await fetch(`${config.apiBase}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const detail = await response
      .json()
      .then((data: { message?: string }) => data.message)
      .catch(() => undefined);
    throw new ApiError(response.status, detail ?? `Request failed (HTTP ${response.status})`);
  }
  return (await response.json()) as T;
};

// ---- Shapes the API returns --------------------------------------------------

export type AuthMethod = 'ssh-key' | 'password' | 'ssh-key-or-password';
export type FolderPermission = 'read-write' | 'read-only' | 'write-only';

export interface Folder {
  path: string;
  permission?: FolderPermission;
  // Bucket-relative prefix this folder points at, when it is not in the user's
  // own directory. `path` stays the name the user sees; this is where it resolves
  // to. Set only for a share into another company's tree -- see the note in the
  // folder editor.
  sharedPath?: string;
  comment?: string;
  // Email the user when a file lands here. Only on a folder they can read, and
  // only if they have an email address -- the API refuses either otherwise.
  notifyOnUpload?: boolean;
}

export interface PublicKey {
  id: string;
  key: string;
  label?: string;
  addedAt: string;
}

export interface Profile {
  username: string;
  authMethod: AuthMethod;
  homeDirectory: string;
  folders: Folder[];
  publicKeys?: PublicKey[];
  allowedSourceIps?: string[];
  comment?: string;
  email?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AccountState {
  exists: boolean;
  enabled: boolean;
  status: string;
}

export type User = Profile & { account: AccountState };

// What a partner gets back about themselves. Deliberately narrower than Profile:
// /api/me does not return homeDirectory, because a partner's paths are always
// home-relative and the bucket layout above their directory is not theirs to see.
export type MyProfile = Omit<Profile, 'homeDirectory'>;

export interface Listing {
  folders: Array<{ path: string; name: string }>;
  files: Array<{
    key: string;
    name: string;
    size: number;
    lastModified?: string;
    storageClass?: string;
  }>;
  continuationToken?: string;
}

export interface NewUser {
  username: string;
  email: string;
  authMethod: AuthMethod;
  folders: Folder[];
  publicKeys?: string[];
  allowedSourceIps?: string[];
  comment?: string;
  password?: string;
  generatePassword?: boolean;
}

// ---- Staff roles ------------------------------------------------------------

export type StaffRole = 'admin' | 'support' | 'viewer';

// Mirrors CAPABILITIES in infrastructure/lib/portal-api/roles.mjs. Only the names
// are repeated here, never the role-to-capability mapping: the list a caller
// actually holds is whatever /whoami returned, so this type can be wrong about
// who gets what only if someone invents a capability, which tsc then catches at
// every call site.
export type Capability =
  | 'self:read'
  | 'roster:read'
  | 'roster:write'
  | 'credentials:write'
  | 'files:list'
  | 'files:read'
  | 'files:write'
  | 'staff:read'
  | 'staff:write';

export interface Whoami {
  username: string;
  email?: string;
  role: StaffRole;
  capabilities: Capability[];
}

// ---- Staff ------------------------------------------------------------------

export interface StaffMember {
  // The Cognito username, which for a federated account is `entraid_<upn>`. Used
  // as the identifier in the role URL and for deciding "is this me".
  username: string;
  email?: string;
  displayName?: string;
  role: StaffRole;
  // False when the role is the default rather than an assigned group, which is a
  // different state worth showing: nobody has decided yet.
  explicit: boolean;
  // Created by Cognito from a SAML assertion, so it has no password. A non
  // federated account is one that can still sign in if Entra is unavailable.
  federated: boolean;
  status: string;
  enabled: boolean;
  createdAt?: string;
}

// ---- Invitations -------------------------------------------------------------

// What a partner is told when they open their link. Deliberately thin: the API
// returns only the name they already supplied and which credentials they are
// being asked for, never the email or folders on the account.
export interface InviteDetails {
  username: string;
  needsPassword: boolean;
  needsKey: boolean;
  expiresAt: string;
}

// What an admin is shown about the outstanding invite for a user, if any.
export interface InviteStatus {
  outstanding: boolean;
  createdAt?: string;
  createdBy?: string;
  expiresAt?: string;
  expired?: boolean;
  usedAt?: string;
  usedFromIp?: string;
}

// The token comes back exactly once, in this response, and is never readable
// again — only its hash is stored. Losing it means issuing a new invite, which
// also revokes this one.
export interface IssuedInvite {
  username: string;
  token: string;
  expiresAt: string;
  needsPassword: boolean;
  needsKey: boolean;
}

// The link a partner opens. The username and token go in the URL *fragment*
// rather than the query string because browsers never send a fragment to a
// server: it stays out of CloudFront and API Gateway access logs, out of
// Referer headers, and out of anything a proxy records. The claim page reads it
// from the address bar and sends it in a request body, which is also unlogged.
export const inviteUrl = ({ username, token }: { username: string; token: string }) =>
  `${window.location.origin}/claim#u=${encodeURIComponent(username)}&t=${encodeURIComponent(token)}`;

export const lookupInvite = (username: string, token: string) =>
  publicRequest<InviteDetails>('/invite/lookup', { username, token });

export const claimInvite = (body: {
  username: string;
  token: string;
  password?: string;
  publicKey?: string;
}) => publicRequest<{ username: string; passwordSet: boolean; keyAdded: boolean }>('/invite/claim', body);

// ---- What the access map returns ---------------------------------------------

export interface AccessFolder {
  // The name the user sees over SFTP. For a share this is an alias, which is
  // exactly why the map is needed: the name says nothing about where it points.
  path: string;
  prefix: string;
  permission: FolderPermission;
  read: boolean;
  write: boolean;
  comment?: string;
  shared: boolean;
  // Whose data the prefix belongs to. `null` means no user's home contains it.
  owner: string | null;
  reachesAnother: boolean;
  wholeHome: boolean;
  wildcard: boolean;
}

export interface AccessUser {
  username: string;
  homeDirectory: string;
  comment?: string;
  folders: AccessFolder[];
}

export interface Exposure {
  owner: string | null;
  reachableBy: Array<{
    username: string;
    via: string;
    prefix: string;
    permission: FolderPermission;
    read: boolean;
    write: boolean;
    wholeHome: boolean;
    wildcard: boolean;
    comment?: string;
  }>;
}

export interface AccessMap {
  users: AccessUser[];
  exposures: Exposure[];
  summary: {
    users: number;
    folders: number;
    shares: number;
    writableShares: number;
    wholeHomeShares: number;
    wildcardShares: number;
    unattributedShares: number;
  };
}

// ---- Admin ------------------------------------------------------------------

export const whoami = () => request<Whoami>('GET', '/admin/whoami');

export const getAccessMap = () => request<AccessMap>('GET', '/admin/access');

export const listStaff = () => request<StaffMember[]>('GET', '/admin/staff');

// Returns `changed: false` when the account already held that role, so the page
// can stay quiet rather than claim it did something.
export const setStaffRole = (username: string, role: StaffRole) =>
  request<{ username: string; role: StaffRole; changed: boolean }>(
    'PUT',
    `/admin/staff/${encodeURIComponent(username)}/role`,
    { role }
  );

export const listUsers = () => request<User[]>('GET', '/admin/users');

export const getUser = (username: string) =>
  request<User>('GET', `/admin/users/${encodeURIComponent(username)}`);

// The password in the response is the only copy that will ever exist outside
// Cognito's hash, and it is present only when one was generated.
export const createUser = (user: NewUser) =>
  request<{ profile: Profile; password?: string }>('POST', '/admin/users', user);

export const updateUser = (username: string, patch: Partial<Profile>) =>
  request<Profile>('PATCH', `/admin/users/${encodeURIComponent(username)}`, patch);

export const deleteUser = (username: string) =>
  request<void>('DELETE', `/admin/users/${encodeURIComponent(username)}`);

export const resetPassword = (username: string, body: { password?: string; generatePassword?: boolean }) =>
  request<{ password?: string }>('PUT', `/admin/users/${encodeURIComponent(username)}/password`, body);

export const setEnabled = (username: string, enabled: boolean) =>
  request<void>('PUT', `/admin/users/${encodeURIComponent(username)}/enabled`, { enabled });

export const getInviteStatus = (username: string) =>
  request<InviteStatus>('GET', `/admin/users/${encodeURIComponent(username)}/invite`);

export const createInvite = (username: string) =>
  request<IssuedInvite>('POST', `/admin/users/${encodeURIComponent(username)}/invite`);

export const addUserKey = (username: string, body: { key: string; label?: string }) =>
  request<PublicKey>('POST', `/admin/users/${encodeURIComponent(username)}/keys`, body);

export const removeUserKey = (username: string, keyId: string) =>
  request<void>(
    'DELETE',
    `/admin/users/${encodeURIComponent(username)}/keys/${encodeURIComponent(keyId)}`
  );

export const listAdminFiles = (prefix: string, continuationToken?: string) =>
  request<Listing>(
    'GET',
    `/admin/files?prefix=${encodeURIComponent(prefix)}` +
      (continuationToken ? `&continuationToken=${encodeURIComponent(continuationToken)}` : '')
  );

export const adminDownloadUrl = (key: string) =>
  request<{ url: string }>('POST', '/admin/files/download-url', { key });

export const adminUploadUrl = (key: string, contentType?: string) =>
  request<{ url: string }>('POST', '/admin/files/upload-url', { key, contentType });

// ---- Partner self-service ---------------------------------------------------

export const getMe = () => request<MyProfile>('GET', '/me');

export const addMyKey = (body: { key: string; label?: string }) =>
  request<PublicKey>('POST', '/me/keys', body);

export const removeMyKey = (keyId: string) =>
  request<void>('DELETE', `/me/keys/${encodeURIComponent(keyId)}`);

export const listMyFiles = (path: string, continuationToken?: string) =>
  request<Listing>(
    'GET',
    `/me/files?path=${encodeURIComponent(path)}` +
      (continuationToken ? `&continuationToken=${encodeURIComponent(continuationToken)}` : '')
  );

export const myDownloadUrl = (path: string) =>
  request<{ url: string }>('POST', '/me/files/download-url', { path });

export const myUploadUrl = (path: string, contentType?: string) =>
  request<{ url: string }>('POST', '/me/files/upload-url', { path, contentType });

// ---- Transfers --------------------------------------------------------------

// Uploads go straight from the browser to S3 with a presigned URL, so the file
// bytes never pass through the API. That is not just a performance choice: API
// Gateway caps a request body at 10 MB, which would make the portal useless for
// exactly the files this system exists to move.
export const uploadToPresignedUrl = async (url: string, file: File): Promise<void> => {
  const response = await fetch(url, {
    method: 'PUT',
    // Must match the ContentType the URL was signed with, or S3 rejects the
    // signature.
    headers: { 'content-type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!response.ok) {
    throw new ApiError(response.status, `Upload failed (HTTP ${response.status})`);
  }
};
